import { Component, inject, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ShowComponent } from 'src/app/common/component/show.component';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ImageUploaderComponent } from '@/common/file-uploader/image-uploader/image-uploader.component';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { AsyncPipe, NgForOf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MenuStates } from '@/common/enums/MenuStates';
import { MenuActive, MenuItem, MenuUploadedImage } from './www-menu.model';
import { Result } from '@/reservation/reservation.model';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { MatSelect } from '@angular/material/select';
import { PageFunction } from '@/common/enums/PageFunction';

export interface TableElement {
	name: string;
	weight: number;
	symbol: string;
}

export enum TextType {
	TEXT_OVER_LISTING = 'textOverListing',
	TEXT_UNDER_LISTING = 'textUnderListing',
}
@Component({
	selector: 'app-www-menu',
	templateUrl: './www-menu.component.html',
	styleUrls: ['./www-menu.component.css'],
	imports: [
		ImageUploaderComponent,
		FormsModule,
		ReactiveFormsModule,
		NavbarModule,
		MatTableModule,
		CKEditorModule,
		NgForOf,
		MatButtonModule,
		RouterModule,
		MatFormFieldModule,
		MatInput,
		MatAutocompleteTrigger,
		MatOption,
		MatAutocomplete,
		AsyncPipe,
		MatSelect,
	],
	standalone: true,
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
	],
})
export class WwwMenuComponent extends ShowComponent {
	@ViewChild('dataTable') table: MatTable<TableElement>;
	@ViewChild(MatSort) sort: MatSort;

	readonly MenuStates = MenuStates;

	filteredMenu: Observable<MenuItem[]>;
	menu: MenuItem[] = [];

	subMenu: MatTableDataSource<MenuItem> = new MatTableDataSource<MenuItem>();
	displayedColumns: string[] = ['id', 'name', 'isActive', 'link', 'edit'];
	menuItemForm: FormGroup;

	data: MenuItem = {
		id: null,
		name: null,
		anchor: null,
		link: null,
		parentMenu: null,
		order: 999,
		text: '',
		isActive: true,
		multimedia: null,
		status: '',
		textOverListing: '',
		textUnderListing: '',
	};
	editor = ClassicEditor;
	editorConfig = {
		toolbar: ['bold', 'italic', '|', 'bulletedList', 'numberedList'],
	};
	editorConfigText = {
		toolbar: ['heading', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList'],
		heading: {
			options: [
				{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
				{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
				{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
				{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
			],
		},
	};
	isActive: MenuActive[] = [
		{ id: true, name: MenuStates.ACTIVE },
		{ id: false, name: MenuStates.INACTIVE },
	];
	status = '';
	TextType = TextType;
	readonly PageFunction = PageFunction;

	protected apiUrl = '/www/menu/';

	protected readonly apiType = 'bag';
	protected readonly parentComponent = 'www/menu';
	protected readonly apiUrlAdd = '/www/menu/';

	private readonly fb = inject(FormBuilder);
	private readonly httpClient = inject(HttpClient);

	patchMenuItem(id: number, payload: Partial<MenuItem>): void {
		this.httpClient.patch(`${environment.apiUrl}/www/menu/${id}/`, payload).subscribe((result) => {
			if (!!result) {
				this.reloadData();
			}
		});
	}

	getApiUrl(): string {
		return '/www/menu/' + this.data.id + '/';
	}

	loadSubMenu(id: number): void {
		this.pageFunction = PageFunction.EDIT;
		this.data.id = id;
		this.router.navigate([`../www/menu/${id}`]);
		this.apiUrl = this.getApiUrl();
		this.loadBefore();
	}

	save(): void {
		this.menuItemForm.markAllAsTouched();
		if (!this.menuItemForm.valid) {
			return;
		}
		this.data = {
			...this.menuItemForm.value,
			multimedia: this.menuItemForm.value.multimedia ? this.menuItemForm.value.multimedia.id : null,
		};
		this.submit();
	}

	uploadImage(event: MenuUploadedImage): void {
		if (!event) {
			return;
		}
		this.menuItemForm.controls.multimedia.patchValue(event);
	}

	displayFn(item: MenuItem): string {
		return item ? item.name : null;
	}

	addItem(): void {
		this.patchMenuItem(this.menuItemForm.get('selectedSubMenu').value.id, {
			parentMenu: this.data.id,
		});
	}

	removeItem(id: number): void {
		this.patchMenuItem(id, {
			parentMenu: null,
		});
	}

	handleBackNavigation(): void {
		if (this.data?.parentMenu) {
			this.loadSubMenu(this.data.parentMenu);
		} else {
			this.router.navigate(['../www/menu/']);
		}
	}

	updateTextFromEditor(event: string, type: TextType): void {
		if (type === TextType.TEXT_OVER_LISTING) {
			this.menuItemForm.controls.textOverListing.patchValue(event);
		} else if (type === TextType.TEXT_UNDER_LISTING) {
			this.menuItemForm.controls.textUnderListing.patchValue(event);
		}
	}

	protected loadBefore(): void {
		this.menuItemForm = this.fb.group({
			id: new FormControl(null, []),
			name: new FormControl('', [Validators.required]),
			link: new FormControl('', [Validators.required]),
			parentMenu: new FormControl(null, []),
			order: new FormControl(999, []),
			text: new FormControl('', []),
			isActive: new FormControl(null, []),
			anchor: new FormControl('', [Validators.required]),
			multimedia: new FormControl('', []),
			textOverListing: new FormControl('', []),
			textUnderListing: new FormControl('', []),
			selectedSubMenu: new FormControl(null, []),
		});

		this.filteredMenu = this.menuItemForm
			.get('selectedSubMenu')
			.valueChanges.pipe(map((value) => this._filter(value || '')));

		this.reloadData();
	}

	protected convertData(data: MenuItem): MenuItem {
		this.menuItemForm.patchValue(data);
		return data;
	}

	private _filter(value: string | MenuItem): MenuItem[] {
		if (typeof value === 'object') {
			value = value.name;
		}

		const filterValue = value.toLowerCase();

		return this.menu.filter(
			(option) =>
				option.name.toLowerCase().includes(filterValue) &&
				option.parentMenu !== Number(this.data.id) &&
				option.id !== Number(this.data.id),
		);
	}

	private reloadData(): void {
		const url = '/www/menu/?limit=999';

		this.bag.get(url, (result: Result<MenuItem>, error) => {
			this.subMenu.data =
				this.pageFunction === PageFunction.ADD
					? []
					: result.results
							.map((obj) => {
								obj.status = obj.isActive ? MenuStates.ACTIVE : MenuStates.INACTIVE;
								return obj;
							})
							.filter((v) => v.parentMenu === Number(this.data.id));

			this.menu = result.results;
			this.menuItemForm.get('selectedSubMenu').setValue(null);
			this.loadData();
		});
	}
}
